import {
  type CSSProperties,
  type ExtractPropTypes,
  computed,
  defineComponent,
  reactive,
  ref,
} from 'vue'

import {
  extend,
  getRootScrollTop,
  getScrollTop,
  getZIndexStyle,
  numericProp,
} from '@xzx-design/utils'
import { useExpose, useNamespace, useParent, useRect } from '@xzx-design/hooks'
import { INDEX_BAR_KEY } from './indexes'

export const indexAnchorProps = {
  index: numericProp,
}

export type IndexAnchorProps = ExtractPropTypes<typeof indexAnchorProps>

const name = 'XzxIndexAnchor'

export default defineComponent({
  name,
  __name: name,
  props: indexAnchorProps,
  setup(props, { slots }) {
    const { bem } = useNamespace('index-anchor')

    const state = reactive({
      top: 0,
      left: null,
      rect: { top: 0, height: 0 },
      width: null,
      active: false,
    })

    const root = ref<HTMLElement>()
    const { parent } = useParent(INDEX_BAR_KEY)

    if (!parent) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(
          '[XzxDesign] <IndexAnchor>  be a child component of <Indexes>.'
        )
      }
      return
    }

    const isSticky = () => state.active && parent.props.sticky

    // eslint-disable-next-line vue/return-in-computed-property
    const anchorStyle = computed<CSSProperties | undefined>(() => {
      const { zIndex, highlightColor } = parent.props

      if (isSticky()) {
        return extend(getZIndexStyle(zIndex), {
          left: state.left ? `${state.left}px` : undefined,
          width: state.width ? `${state.width}px` : undefined,
          transform: state.top
            ? `translate3d(0, ${state.top}px, 0)`
            : undefined,
          color: highlightColor,
        })
      }
    })

    const getRect = (
      scrollParent: Window | Element,
      scrollParentRect: { top: number }
    ) => {
      const rootRect = useRect(root)
      state.rect.height = rootRect.height

      if (scrollParent === window || scrollParent === document.body) {
        state.rect.top = rootRect.top + getRootScrollTop()
      } else {
        state.rect.top =
          rootRect.top + getScrollTop(scrollParent) - scrollParentRect.top
      }

      return state.rect
    }

    useExpose({
      state,
      getRect,
    })

    return () => {
      const sticky = isSticky()

      return (
        <div
          ref={root}
          style={{ height: sticky ? `${state.rect.height}px` : undefined }}
        >
          <div style={anchorStyle.value} class={[bem({ sticky })]}>
            {slots.default ? slots.default() : props.index}
          </div>
        </div>
      )
    }
  },
})
